'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import dayjs from 'dayjs'
import Link from 'next/link'

import { ClockCircleOutlined, RightOutlined } from '@ant-design/icons'
import { Button, Card, Divider, Spin, Tabs } from 'antd'
import {
  ArrowTopRightOnSquareIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline'

import { getScopeFromCookie } from '@/fe-common/utils'
import { isLTC } from '@/config'
import { useSelector } from '@/context/app-context'
import { useMonthlyBillingData } from '@/hooks/useMonthlyBillingData'
import { getBalanceInfo, getMessageList } from '@/service/bills'
import { getUserCouponStats } from '@/service/coupons'
import Money from '@/app/components/mlops/Money'
import BillingGuard, { useBillingEnabled } from '@/app/components/base/billing-guard'

const BillingCenter = () => {
  const { t } = useTranslation()
  const [showBalance, setShowBalance] = useState(false)
  const [balanceInfo, setBalanceInfo] = useState<{
    amount: number
    currency: string
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [messages, setMessages] = useState<any[]>([])
  const [messagesLoading, setMessagesLoading] = useState(true)
  const [total, setTotal] = useState(0)

  // 获取当前用户信息
  const userProfile = useSelector(state => state.userProfile)

  // 获取当月收支数据
  const {
    data: monthlyBillingData,
    loading: monthlyBillingLoading,
    error: monthlyBillingError,
  } = useMonthlyBillingData(userProfile?.id.replace(/-/g, ''))

  useEffect(() => {
    const fetchBalanceInfo = async () => {
      try {
        const balance = await getBalanceInfo()
        setBalanceInfo(balance)
      } catch (error) {
        console.error('Failed to fetch balance info:', error)
      } finally {
        setIsLoading(false)
      }
    }
    const fetchMessages = async () => {
      try {
        const res = await getMessageList({
          contact_type: 'webconsole',
          limit: 5,
          offset: 0,
          scope: getScopeFromCookie(),
        })
        setMessages(res?.data)
        setTotal(res?.total)
      } catch (error) {
        console.error('Failed to fetch messages:', error)
      } finally {
        setMessagesLoading(false)
      }
    }

    fetchBalanceInfo()
    fetchMessages()
  }, [])

  // 获取用户优惠券数量统计
  const { data: couponStats, isLoading: couponStatsLoading } = useSWR(
    '/api/coupons/stats',
    getUserCouponStats,
  )

  const toggleBalanceVisibility = () => {
    setShowBalance(!showBalance)
  }

  const handleBillingCenterClick = () => {
    window.open('/llm/bill/account-overview', '_blank')
  }

  const handleRechargeClick = () => {
    window.open('/llm/bill/recharge', '_blank')
  }

  const handleInvoiceClick = () => {
    window.open('/llm/bill/invoice', '_blank')
  }

  const billingEnabled = useBillingEnabled()
  const hasBillingContent = billingEnabled
  const hasLTCContent = isLTC
  
  // 如果既没有 billing 内容也没有 LTC 内容，则不渲染
  if (!hasBillingContent && !hasLTCContent) {
    return null
  }

  return (
    <section className='w-[400px]'>
      <BillingGuard>
        <Card className='mb-4'>
          <div className='flex flex-col gap-4'>
            <div>
              <div
                className='flex justify-between items-center py-2 rounded-md transition-colors cursor-pointer hover:bg-gray-50'
                onClick={handleBillingCenterClick}>
                <h2 className='text-lg font-medium text-gray-900'>
                  {t('nav.bill.feeCenter')}
                </h2>
                <RightOutlined />
              </div>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-base font-medium text-gray-900'>
                {t('ecs.billing.assetAccount')}
              </span>
              <div className='flex gap-2'>
                <Button
                  onClick={handleRechargeClick}
                  className='flex gap-2 items-center'>
                  {t('ecs.billing.rechargeRemittance')}{' '}
                  <ArrowTopRightOnSquareIcon className='w-4 h-4' />
                </Button>
                <Button
                  onClick={handleInvoiceClick}
                  className='flex gap-2 items-center'>
                  {t('ecs.billing.invoice')}{' '}
                  <ArrowTopRightOnSquareIcon className='w-4 h-4' />
                </Button>
              </div>
            </div>
            <div className='flex flex-col gap-2'>
              <div className='flex items-center'>
                {t('ecs.billing.availableBalance')}
                <button
                  onClick={toggleBalanceVisibility}
                  className='inline-flex items-center ml-1 transition-opacity hover:opacity-70'>
                  {showBalance ? (
                    <EyeIcon className='w-4 h-4' />
                  ) : (
                    <EyeSlashIcon className='w-4 h-4' />
                  )}
                </button>
              </div>
              <div className='text-2xl'>
                {isLoading ? (
                  <span className='text-gray-400'>
                    {t('ecs.billing.loading')}
                  </span>
                ) : showBalance ? (
                  <Money
                    value={balanceInfo?.amount || 0}
                    currency={balanceInfo?.currency || 'CNY'}
                  />
                ) : (
                  <span className='text-gray-500'>****</span>
                )}
              </div>
            </div>
          </div>
          <Divider />
          <div className='flex flex-col gap-2'>
            <h3 className='text-base font-medium text-gray-900'>
              {t('ecs.billing.incomeExpenseOverview')}
            </h3>
            <div className='flex justify-between items-center px-6 py-4 text-base bg-gray-100 rounded-md'>
              <span>{t('ecs.billing.monthlyIncome')}</span>
              <strong>
                {monthlyBillingLoading ? (
                  <Spin size='small' />
                ) : monthlyBillingError ? (
                  <span className='text-red-500'>
                    {t('ecs.billing.loadingFailed')}
                  </span>
                ) : (
                  <Money
                    value={monthlyBillingData?.incomeAmount || 0}
                    currency='CNY'
                  />
                )}
              </strong>
            </div>
            <div className='flex justify-between items-center px-6 py-4 text-base bg-gray-100 rounded-md'>
              <span>{t('ecs.billing.monthlyOrderExpenses')}</span>
              <strong>
                {monthlyBillingLoading ? (
                  <Spin size='small' />
                ) : monthlyBillingError ? (
                  <span className='text-red-500'>
                    {t('ecs.billing.loadingFailed')}
                  </span>
                ) : (
                  <Money
                    value={monthlyBillingData?.expenditureAmount || 0}
                    currency='CNY'
                  />
                )}
              </strong>
            </div>
          </div>
          <Divider />
          <Spin spinning={couponStatsLoading}>
            <div className='flex flex-col gap-2'>
              <h3 className='text-base font-medium text-gray-900'>
                {t('ecs.billing.myBenefits')}
              </h3>
              <div className='flex flex-col gap-2 justify-between items-center'>
                <Link
                  href='/bill/coupons'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='w-full flex justify-between items-center px-6 py-4 text-base bg-gray-100 rounded-md hover:bg-gray-200 transition-colors cursor-pointer'>
                  <span>{t('ecs.billing.activeCoupons')}</span>
                  <strong>{couponStats?.activeCount || 0}</strong>
                </Link>
                <Link
                  href='/bill/coupons'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='w-full flex justify-between items-center px-6 py-4 text-base bg-gray-100 rounded-md hover:bg-gray-200 transition-colors cursor-pointer'>
                  <span>{t('ecs.billing.totalCoupons')}</span>
                  <strong>{couponStats?.allCount || 0}</strong>
                </Link>
              </div>
            </div>
          </Spin>
        </Card>
      </BillingGuard>
      {isLTC && (
        <Card>
          <h2 className='text-lg font-medium text-gray-900'>
            {t('ecs.billing.productInformation')}
          </h2>
          <Tabs
            tabBarExtraContent={
              total > 3 && (
                <a
                  href='/iaas/notification'
                  target='_blank'
                  rel='noopener noreferrer'>
                  <Button type='link' className='!px-0 text-xs'>
                    {t('ecs.billing.viewMore')}
                  </Button>
                </a>
              )
            }
            items={[
              {
                key: '1',
                label: t('ecs.billing.informationNotifications'),
                children: messagesLoading ? (
                  <Spin size='small' />
                ) : (
                  <ul className='flex flex-col gap-3'>
                    {messages.map(message => (
                      <li key={message.id}>
                        <h3 className='mb-1 text-sm font-medium text-gray-900'>
                          {message.title}
                        </h3>
                        <time className='flex gap-1 items-center text-sm text-gray-500'>
                          <ClockCircleOutlined />
                          {dayjs(message.created_at).format(
                            'YYYY-MM-DD HH:mm:ss',
                          )}
                        </time>
                      </li>
                    ))}
                  </ul>
                ),
              },
            ]}
          />
        </Card>
      )}
    </section>
  )
}

export default BillingCenter
